import { EventEmitter, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Store } from '@ngxs/store';
import { HelperFunctionsService } from '../helpers/helper-functions.service';
import { PuzzleFrameworkService } from '../../puzzle-framework-module/puzzle-framework.service';
import { Md5 } from 'ts-md5';

export enum PuzzleKeyCodes {
  MoveLeft = 37,
  MoveRight = 39,
  MoveUp = 38,
  MoveDown = 40,
  ChangeDirection = 32,
  Delete = 8,
}

export enum PuzzleSpecialKeys {
  Shift = 'Shift',
  ArrowLeft = 'ArrowLeft',
  ArrowUp = 'ArrowUp',
  ArrowRight = 'ArrowRight',
  ArrowDown = 'ArrowDown',
  Alt = 'Alt',
  Tab = 'Tab',
  ShiftTab = 'ShiftTab',
  Escape = 'Escape',
}

@Injectable({
  providedIn: 'root',
})
export class PuzzleKeyboardService {
  onKeyDown = new EventEmitter<string>();
  /*
   * Enter is not part of PuzzleSpecialKeys because the existing boards react to that enum
   * with cursor movement. It gets its own emitter so a board can opt in without changing
   * the behaviour of the others.
   */
  onEnter = new EventEmitter<void>();
  onSpecialKeyDown: EventEmitter<PuzzleSpecialKeys> = new EventEmitter<PuzzleSpecialKeys>();
  onSpecialKeyUp = new EventEmitter<string>();
  onCapsLock = new EventEmitter<boolean>();

  keyFilterRegex: RegExp;
  private softKeyboardOpen: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  readonly softKeyboardOpen$: Observable<boolean> = this.softKeyboardOpen.asObservable();

  private keepSoftkeyboardOpen = false;
  private lastPressed = '';

  private isDisabled = false;

  constructor(
    private store: Store,
    private helperFunctions: HelperFunctionsService,
    private framework: PuzzleFrameworkService
  ) {
    this.store
      .select((state) => state.puzzleGameState.keepKeyboardOpen)
      .subscribe((keepOpen) => {
        this.keepSoftkeyboardOpen = keepOpen;
        if (keepOpen) {
          this.openSoftKeyboard();
        }
      });
  }

  openSoftKeyboard() {
    if (!this.helperFunctions.isMobileDevice()) {
      return;
    }
    this.softKeyboardOpen.next(true);
  }

  closeSoftKeyboard() {
    if (this.keepSoftkeyboardOpen) {
      return;
    }
    this.softKeyboardOpen.next(false);
  }

  onWindowKeyDown(event: KeyboardEvent): void {
    if (event.keyCode !== 20) {
      return;
    }
    const capsOn = !(event.getModifierState && event.getModifierState('CapsLock'));
    this.onCapsLock.emit(capsOn);
  }

  onDocumentKeyUp(event: KeyboardEvent) {
    if (Object.values(PuzzleSpecialKeys).indexOf(event.key as PuzzleSpecialKeys) !== -1) {
      this.onSpecialKeyUp.emit(event.key);
    }
  }

  onDocumentKeyDown(event: KeyboardEvent) {
    // .sentry-error-embed-wrapper
    // .sentry-error-embed
    // Skip if an html element with a class of sentry-error-embed-wrapper is present.
    if (document.querySelector('.sentry-error-embed-wrapper') !== null) {
      return;
    }

    // some other dialog or service may disable us
    if (this.isDisabled) {
      return;
    }

    if (Object.values(PuzzleSpecialKeys).indexOf(event.key as PuzzleSpecialKeys) !== -1) {
      if (event.key === PuzzleSpecialKeys.Tab && !event.shiftKey) {
        this.onSpecialKeyDown.emit(PuzzleSpecialKeys.Tab);
      } else if (event.key === PuzzleSpecialKeys.Tab && event.shiftKey) {
        this.onSpecialKeyDown.emit(PuzzleSpecialKeys.ShiftTab);
      } else {
        this.onSpecialKeyDown.emit(event.key as PuzzleSpecialKeys);
      }
    }

    if (event.key === 'Enter') {
      this.onEnter.emit();
    }

    let key: string;

    const digitKeys = [
      'Digit1',
      'Digit2',
      'Digit3',
      'Digit4',
      'Digit5',
      'Digit6',
      'Digit7',
      'Digit8',
      'Digit9',
      'Digit0',
    ];

    if (digitKeys.indexOf(event.code) !== -1) {
      key = event.code.replace('Digit', '');
    } else {
      key = event.key;
    }

    this.checkRumpelstilzchen(key);

    // if (this.toUpperCase === true) {
    //   key = key.toUpperCase();
    // }

    if (this.keyFilterRegex) {
      key = key.replace(this.keyFilterRegex, '');
    }

    if (key.length === 1) {
      // this.puzzleKeyDown.emit(key);
      this.onKeyDown.emit(key);
    } else if (event.keyCode === PuzzleKeyCodes.Delete) {
      // this.puzzleKeyDown.emit('');
      this.onKeyDown.emit('');
    }
    event.preventDefault();
  }

  softKeyboardEmit(key: string) {
    this.checkRumpelstilzchen(key);
    this.onKeyDown.emit(key);
  }

  private checkRumpelstilzchen(key: string) {
    this.lastPressed = (this.lastPressed + key.toLowerCase()).slice(-7);
    if (
      (this.framework.puzzleFrameworkConfig.rumpelstilzchenReload || []).includes(
        Md5.hashStr(this.lastPressed).toString()
      )
    ) {
      this.framework.toggleRumpelstilzchen(true);
    } else if (
      (this.framework.puzzleFrameworkConfig.rumpelstilzchenNoReload || []).includes(
        Md5.hashStr(this.lastPressed).toString()
      )
    ) {
      console.info('Developer mode activated');
      void this.framework.showTranslatedToast('PUZZLE.developer_mode');
      this.framework.toggleRumpelstilzchen(false);
    }
  }

  public disable() {
    this.isDisabled = true;
  }

  public enable() {
    this.isDisabled = false;
  }
}
